/**
 * 🎯 HOOK DE GESTION DES CONFLITS
 * 
 * Hook réutilisable pour gérer les conflits de nœuds lors de la création
 * S'intègre avec le middleware backend de détection (HTTP 409)
 * 
 * @module useConflictResolution
 * @version 1.0.0
 */

import { useState } from 'react';
import apiClient from '../utils/apiClient';

/**
 * Hook pour gérer la détection et résolution des conflits de nœuds
 * 
 * @returns {Object} État et fonctions de gestion des conflits
 * @property {Object|null} conflict - Données du conflit détecté
 * @property {Function} setConflict - Setter manuel du conflit
 * @property {boolean} isResolving - Indique si une résolution est en cours
 * @property {Function} createNodeWithConflictHandling - Créer un nœud avec gestion auto des conflits
 * @property {Function} resolveConflict - Résoudre un conflit (enrichir OU créer)
 * 
 * @example
 * ```jsx
 * const { conflict, createNodeWithConflictHandling, resolveConflict } = useConflictResolution();
 * 
 * // Dans handleSubmit
 * const result = await createNodeWithConflictHandling(nodeData);
 * if (result.hasConflict) {
 *   // Le modal s'affiche automatiquement (conflict !== null)
 *   return;
 * }
 * 
 * // Dans modal
 * <ConflictResolutionModal 
 *   conflict={conflict}
 *   onResolve={resolveConflict}
 * />
 * ```
 */
export function useConflictResolution() {
  const [conflict, setConflict] = useState(null);
  const [isResolving, setIsResolving] = useState(false);
  const [conflictQueue, setConflictQueue] = useState([]);
  const [currentConflictIndex, setCurrentConflictIndex] = useState(0);
  const [resolutionResults, setResolutionResults] = useState([]);

  /**
   * 🎯 CRÉER UN NŒUD AVEC GESTION AUTOMATIQUE DES CONFLITS
   * 
   * Envoie la requête de création et gère automatiquement le code 409
   * 
   * @param {Object} nodeData - Données du nœud à créer
   * @param {string} endpoint - Endpoint API (défaut: '/node')
   * @param {Object} additionalBody - Données supplémentaires à envoyer dans le body (optionnel)
   * @returns {Promise<Object>} Résultat de la création
   * @returns {boolean} result.success - Succès de la création
   * @returns {boolean} result.hasConflict - Conflit détecté (409)
   * @returns {Object} result.node - Nœud créé (si succès)
   */
  const createNodeWithConflictHandling = async (nodeData, endpoint = '/node', additionalBody = null) => {
    try {
      console.log('🎯 [CONFLICT_HOOK] Tentative création:', nodeData.name);
      
      // Si additionalBody fourni, l'utiliser directement; sinon envoyer nodeData
      const requestBody = additionalBody || nodeData;
      
      const response = await apiClient.post(endpoint, requestBody);
      
      console.log('✅ [CONFLICT_HOOK] Nœud créé avec succès');
      return { 
        success: true, 
        node: response.data.node || response.data.nodes?.[0] || response.data
      };

    } catch (error) {
      // 🚨 CONFLIT DÉTECTÉ (409)
      if (error.response?.status === 409) {
        const responseData = error.response.data;
        
        // Format conflit unique (conflict + conflictType)
        if (responseData.conflict) {
          console.log('⚠️ [CONFLICT_HOOK] Conflit détecté:', responseData.conflictType);
          setConflict(responseData);
          
          return { 
            success: false, 
            hasConflict: true,
            conflictData: responseData
          };
        }
        
        // Format conflit multiple (conflicts array)
        if (responseData.conflicts && responseData.conflicts.length > 0) {
          console.log('⚠️ [CONFLICT_HOOK] Conflits multiples détectés:', responseData.conflicts.length);
          
          // Initialiser la file d'attente des conflits
          setConflictQueue(responseData.conflicts);
          setCurrentConflictIndex(0);
          setResolutionResults([]);
          
          // Afficher le premier conflit
          setConflict({
            ...responseData.conflicts[0],
            isMultiple: true,
            currentIndex: 0,
            totalConflicts: responseData.conflicts.length
          });
          
          return { 
            success: false, 
            hasConflict: true,
            conflictData: responseData.conflicts[0],
            multipleConflicts: true,
            totalConflicts: responseData.conflicts.length,
            allConflicts: responseData.conflicts
          };
        }
      }
      
      // ❌ AUTRE ERREUR
      console.error('❌ [CONFLICT_HOOK] Erreur création:', error);
      throw error;
    }
  };

  /**
   * 🔄 RÉSOUDRE UN CONFLIT
   * 
   * Deux actions possibles :
   * - 'enrich': Enrichir le nœud existant
   * - 'create': Créer un nouveau nœud distinct (force=true)
   * 
   * @param {Object} resolution - Données de résolution
   * @param {string} resolution.action - 'enrich' ou 'create'
   * @param {Object} resolution.existingNode - Nœud existant (pour enrichir)
   * @param {Object} resolution.newData - Nouvelles données
   * @returns {Promise<Object>} Résultat de la résolution
   */
  const resolveConflict = async (resolution) => {
    setIsResolving(true);

    try {
      let result;
      
      if (resolution.action === 'enrich') {
        console.log('🔄 [CONFLICT_HOOK] Enrichissement du nœud:', resolution.existingNode.name);
        
        // Enrichir le nœud existant avec toutes les nouvelles données
        const response = await apiClient.patch(
          `/node/${resolution.existingNode.id || resolution.existingNode.name}/enrich`,
          resolution.newData
        );
        
        console.log('✅ [CONFLICT_HOOK] Nœud enrichi avec succès');
        
        result = { 
          success: true, 
          action: 'enriched',
          node: response.data.node,
          enrichedFields: response.data.enrichedFields
        };

      } else if (resolution.action === 'merge') {
        console.log('🔀 [CONFLICT_HOOK] Merge personnalisé du nœud:', resolution.existingNode.name);
        console.log('📦 [CONFLICT_HOOK] Données fusionnées:', resolution.mergedData);
        
        // Enrichir avec les données fusionnées personnalisées
        const response = await apiClient.patch(
          `/node/${resolution.existingNode.id || resolution.existingNode.name}/enrich`,
          resolution.mergedData
        );
        
        console.log('✅ [CONFLICT_HOOK] Merge appliqué avec succès');
        
        result = { 
          success: true, 
          action: 'merged',
          node: response.data.node,
          enrichedFields: response.data.enrichedFields
        };

      } else if (resolution.action === 'create') {
        console.log('➕ [CONFLICT_HOOK] Création nœud distinct (force)');
        
        // Créer un nouveau nœud (force=true pour bypasser la détection)
        const response = await apiClient.post('/node?force=true', resolution.newData);
        
        console.log('✅ [CONFLICT_HOOK] Nouveau nœud créé');
        
        result = { 
          success: true, 
          action: 'created',
          node: response.data 
        };

      } else {
        throw new Error(`Action inconnue: ${resolution.action}`);
      }

      // Sauvegarder le résultat
      setResolutionResults(prev => [...prev, result]);

      // 🔄 S'il reste des conflits dans la file d'attente
      if (conflictQueue.length > 0 && currentConflictIndex < conflictQueue.length - 1) {
        const nextIndex = currentConflictIndex + 1;
        setCurrentConflictIndex(nextIndex);
        
        console.log(`⏭️ [CONFLICT_HOOK] Conflit suivant: ${nextIndex + 1}/${conflictQueue.length}`);
        
        // Afficher le conflit suivant
        setConflict({
          ...conflictQueue[nextIndex],
          isMultiple: true,
          currentIndex: nextIndex,
          totalConflicts: conflictQueue.length
        });
        
        setIsResolving(false);
        return result;
      }

      // ✅ Tous les conflits sont résolus
      console.log('✅ [CONFLICT_HOOK] Tous les conflits résolus:', resolutionResults.length + 1);
      
      setIsResolving(false);
      setConflict(null);
      setConflictQueue([]);
      setCurrentConflictIndex(0);
      
      return {
        ...result,
        allResolved: true,
        totalResolutions: resolutionResults.length + 1,
        allResults: [...resolutionResults, result]
      };

    } catch (error) {
      console.error('❌ [CONFLICT_HOOK] Erreur résolution:', error);
      setIsResolving(false);
      throw error;
    }
  };

  return {
    conflict,
    setConflict,
    isResolving,
    createNodeWithConflictHandling,
    resolveConflict,
    // Nouvelles propriétés pour conflits multiples
    conflictQueue,
    currentConflictIndex,
    resolutionResults,
    hasMultipleConflicts: conflictQueue.length > 1,
    remainingConflicts: conflictQueue.length - currentConflictIndex - 1,
  };
}
